/**
 * Rectangular-graph adapter for Azgaar Fantasy Map Generator's template
 * heightmap operations.
 *
 * Adapted from:
 * https://github.com/Azgaar/Fantasy-Map-Generator/blob/4eb71cfb89bebf987174623f9ccc0485bf328819/src/generators/heightmap-generator.ts
 *
 * Copyright (c) 2017-2024 Max Haniyeu (Azgaar)
 * SPDX-License-Identifier: MIT
 * See ./LICENSE and ./UPSTREAM.md.
 */

import { AZGAAR_HEIGHTMAP_TEMPLATES, type AzgaarHeightmapTemplateId } from "./heightmap-templates";

type HeightmapTool = "Hill" | "Pit" | "Range" | "Trough" | "Strait" | "Mask" | "Invert" | "Add" | "Multiply" | "Smooth";

export interface AzgaarHeightmapOptions {
  columns: number;
  rows: number;
  seed: string;
  template: AzgaarHeightmapTemplateId;
}

const clamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, value));
const limitHeight = (value: number) => clamp(Math.round(value), 0, 100);

function seedHash(seed: string) {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function seededRandom(seed: string) {
  let state = seedHash(seed) || 0x6d2b79f5;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ value >>> 15, value | 1);
    value ^= value + Math.imul(value ^ value >>> 7, value | 61);
    return ((value ^ value >>> 14) >>> 0) / 4294967296;
  };
}

function randomInteger(random: () => number, minimum: number, maximum: number) {
  return Math.floor(random() * (maximum - minimum + 1)) + minimum;
}

function chance(random: () => number, probability: number) {
  if (probability >= 1) return true;
  if (probability <= 0) return false;
  return random() < probability;
}

/** Port of Azgaar's getNumberInRange syntax used by template scripts. */
function numberInRange(random: () => number, source: string) {
  if (!Number.isNaN(+source)) {
    const numeric = +source;
    return Math.trunc(numeric) + (chance(random, numeric - Math.trunc(numeric)) ? 1 : 0);
  }
  const sign = source[0] === "-" ? -1 : 1;
  const unsigned = Number.isNaN(+source[0]!) ? source.slice(1) : source;
  const parts = unsigned.split("-");
  if (parts.length !== 2) return 0;
  return randomInteger(random, parseFloat(parts[0]!) * sign, parseFloat(parts[1]!));
}

class RectangularHeightmapKernel {
  private readonly columns: number;
  private readonly rows: number;
  private readonly count: number;
  private readonly random: () => number;
  private readonly blobPower: number;
  private readonly linePower: number;
  private heights: Uint8Array;

  constructor(options: AzgaarHeightmapOptions) {
    this.columns = Math.max(8, Math.round(options.columns));
    this.rows = Math.max(8, Math.round(options.rows));
    this.count = this.columns * this.rows;
    this.random = seededRandom(`${options.seed}:azgaar:${options.template}:4eb71cfb`);
    this.blobPower = this.getBlobPower(this.count);
    this.linePower = this.getLinePower(this.count);
    this.heights = new Uint8Array(this.count);
  }

  generate(template: AzgaarHeightmapTemplateId) {
    const script = AZGAAR_HEIGHTMAP_TEMPLATES[template].script;
    for (const line of script.split("\n")) {
      const [tool, a2 = "0", a3 = "0", a4 = "0", a5 = "0"] = line.trim().split(/\s+/) as [HeightmapTool, string, string, string, string];
      this.addStep(tool, a2, a3, a4, a5);
    }
    return this.heights;
  }

  private getBlobPower(cells: number) {
    const powers: [number, number][] = [[1_000, .93], [2_000, .95], [5_000, .97], [10_000, .98], [20_000, .99], [30_000, .991], [40_000, .993], [50_000, .994], [60_000, .995], [70_000, .9955], [80_000, .996], [90_000, .9964], [100_000, .9973]];
    return powers.find(([threshold]) => cells <= threshold)?.[1] ?? .9973;
  }

  private getLinePower(cells: number) {
    const powers: [number, number][] = [[1_000, .75], [2_000, .77], [5_000, .79], [10_000, .81], [20_000, .82], [30_000, .83], [40_000, .84], [50_000, .86], [60_000, .87], [70_000, .88], [80_000, .91], [90_000, .92], [100_000, .93]];
    return powers.find(([threshold]) => cells <= threshold)?.[1] ?? .93;
  }

  private pointInRange(source: string, length: number) {
    const [minimumSource, maximumSource = minimumSource] = source.split("-");
    const minimum = parseInt(minimumSource!, 10) / 100 || 0;
    const maximum = parseInt(maximumSource!, 10) / 100 || minimum;
    return randomInteger(this.random, Math.round(minimum * (length - 1)), Math.round(maximum * (length - 1)));
  }

  private cellAt(x: number, y: number) {
    const column = clamp(Math.round(x), 0, this.columns - 1);
    const row = clamp(Math.round(y), 0, this.rows - 1);
    return row * this.columns + column;
  }

  private point(cell: number): [number, number] {
    return [cell % this.columns, Math.floor(cell / this.columns)];
  }

  private neighbors(cell: number) {
    const x = cell % this.columns;
    const y = Math.floor(cell / this.columns);
    const result: number[] = [];
    if (x > 0) result.push(cell - 1);
    if (x < this.columns - 1) result.push(cell + 1);
    if (y > 0) result.push(cell - this.columns);
    if (y < this.rows - 1) result.push(cell + this.columns);
    // Azgaar's production grid is an irregular Voronoi graph with roughly six
    // neighbours per cell. Alternating diagonals give this rectangular adapter
    // the same six-way propagation character and remove four-axis plateaus.
    const diagonalX = y % 2 === 0 ? x - 1 : x + 1;
    if (diagonalX >= 0 && diagonalX < this.columns && y > 0) result.push((y - 1) * this.columns + diagonalX);
    if (diagonalX >= 0 && diagonalX < this.columns && y < this.rows - 1) result.push((y + 1) * this.columns + diagonalX);
    return result;
  }

  private addHill(count: string, height: string, rangeX: string, rangeY: string) {
    const desired = numberInRange(this.random, count);
    for (let hill = 0; hill < desired; hill += 1) {
      const change = new Uint8Array(this.count);
      const amplitude = limitHeight(numberInRange(this.random, height));
      let start = 0;
      for (let attempt = 0; attempt < 50; attempt += 1) {
        start = this.cellAt(this.pointInRange(rangeX, this.columns), this.pointInRange(rangeY, this.rows));
        if (this.heights[start]! + amplitude <= 90) break;
      }
      change[start] = amplitude;
      const queue = [start];
      for (let cursor = 0; cursor < queue.length; cursor += 1) {
        const current = queue[cursor]!;
        for (const neighbor of this.neighbors(current)) {
          if (change[neighbor]) continue;
          change[neighbor] = limitHeight(change[current]! ** this.blobPower * (this.random() * .2 + .9));
          if (change[neighbor]! > 1) queue.push(neighbor);
        }
      }
      this.heights = this.heights.map((value, index) => limitHeight(value + change[index]!));
    }
  }

  private addPit(count: string, height: string, rangeX: string, rangeY: string) {
    const desired = numberInRange(this.random, count);
    for (let pit = 0; pit < desired; pit += 1) {
      const used = new Uint8Array(this.count);
      let start = 0;
      for (let attempt = 0; attempt < 50; attempt += 1) {
        start = this.cellAt(this.pointInRange(rangeX, this.columns), this.pointInRange(rangeY, this.rows));
        if (this.heights[start]! >= 20) break;
      }
      let amplitude = limitHeight(numberInRange(this.random, height));
      const queue = [start];
      for (let cursor = 0; cursor < queue.length; cursor += 1) {
        const current = queue[cursor]!;
        amplitude = amplitude ** this.blobPower * (this.random() * .2 + .9);
        if (amplitude < 1) break;
        for (const neighbor of this.neighbors(current)) {
          if (used[neighbor]) continue;
          this.heights[neighbor] = limitHeight(this.heights[neighbor]! - amplitude * (this.random() * .2 + .9));
          used[neighbor] = 1;
          queue.push(neighbor);
        }
      }
    }
  }

  private greedyLine(start: number, end: number, randomness: number, preventRevisit = true) {
    const used = new Uint8Array(this.count);
    const range = [start];
    let current = start;
    used[current] = 1;
    for (let guard = 0; current !== end && guard < this.count; guard += 1) {
      const [endX, endY] = this.point(end);
      let best = -1;
      let bestDistance = Number.POSITIVE_INFINITY;
      for (const neighbor of this.neighbors(current)) {
        if (preventRevisit && used[neighbor]) continue;
        const [x, y] = this.point(neighbor);
        let distance = (endX - x) ** 2 + (endY - y) ** 2;
        if (this.random() > 1 - randomness) distance /= 2;
        if (distance < bestDistance) { bestDistance = distance; best = neighbor; }
      }
      if (best < 0) break;
      current = best;
      used[current] = 1;
      range.push(current);
    }
    return range;
  }

  private randomLineEndpoints(rangeX: string, rangeY: string, requireLandStart: boolean) {
    let start = 0;
    let startX = 0;
    let startY = 0;
    for (let attempt = 0; attempt < 50; attempt += 1) {
      startX = this.pointInRange(rangeX, this.columns);
      startY = this.pointInRange(rangeY, this.rows);
      start = this.cellAt(startX, startY);
      if (!requireLandStart || this.heights[start]! >= 20) break;
    }
    let endX = startX;
    let endY = startY;
    for (let attempt = 0; attempt < 50; attempt += 1) {
      endX = this.random() * (this.columns - 1) * .8 + (this.columns - 1) * .1;
      endY = this.random() * (this.rows - 1) * .7 + (this.rows - 1) * .15;
      const distance = Math.abs(endY - startY) + Math.abs(endX - startX);
      if (distance >= this.columns / 8 && distance <= this.columns / (requireLandStart ? 2 : 3)) break;
    }
    return [start, this.cellAt(endX, endY)] as const;
  }

  private addRangeLike(count: string, height: string, rangeX: string, rangeY: string, direction: 1 | -1, randomness: number) {
    const desired = numberInRange(this.random, count);
    for (let lineIndex = 0; lineIndex < desired; lineIndex += 1) {
      const [start, end] = this.randomLineEndpoints(rangeX, rangeY, direction < 0);
      const range = this.greedyLine(start, end, randomness);
      const used = new Uint8Array(this.count);
      for (const cell of range) used[cell] = 1;
      let amplitude = limitHeight(numberInRange(this.random, height));
      let queue = range.slice();
      let spread = 0;
      while (queue.length) {
        const frontier = queue;
        queue = [];
        spread += 1;
        for (const cell of frontier) this.heights[cell] = limitHeight(this.heights[cell]! + direction * amplitude * (this.random() * .3 + .85));
        amplitude = amplitude ** this.linePower - 1;
        if (amplitude < 2) break;
        for (const cell of frontier) for (const neighbor of this.neighbors(cell)) if (!used[neighbor]) { queue.push(neighbor); used[neighbor] = 1; }
      }
      for (let index = 0; index < range.length; index += 6) {
        let current = range[index]!;
        for (let step = 0; step < spread; step += 1) {
          const neighbors = this.neighbors(current);
          if (!neighbors.length) break;
          const downhill = neighbors.reduce((best, candidate) => this.heights[candidate]! < this.heights[best]! ? candidate : best, neighbors[0]!);
          this.heights[downhill] = limitHeight((this.heights[current]! * 2 + this.heights[downhill]!) / 3);
          current = downhill;
        }
      }
    }
  }

  private addStrait(width: string, direction = "vertical") {
    const desiredWidth = Math.min(numberInRange(this.random, width), this.columns / 3);
    if (desiredWidth < 1 && chance(this.random, desiredWidth)) return;
    const vertical = direction === "vertical";
    const startX = vertical ? Math.floor(this.random() * this.columns * .4 + this.columns * .3) : 1;
    const startY = vertical ? 1 : Math.floor(this.random() * this.rows * .4 + this.rows * .3);
    const endX = vertical ? Math.floor(this.columns - startX - this.columns * .1 + this.random() * this.columns * .2) : this.columns - 2;
    const endY = vertical ? this.rows - 2 : Math.floor(this.rows - startY - this.rows * .1 + this.random() * this.rows * .2);
    let range = this.greedyLine(this.cellAt(startX, startY), this.cellAt(endX, endY), .2, false);
    const used = new Uint8Array(this.count);
    const step = .1 / Math.max(desiredWidth, .1);
    for (let spread = 0; spread < desiredWidth; spread += 1) {
      const expansion: number[] = [];
      const exponent = .9 - step * (desiredWidth - spread);
      for (const cell of range) for (const neighbor of this.neighbors(cell)) {
        if (used[neighbor]) continue;
        used[neighbor] = 1;
        expansion.push(neighbor);
        const carved = this.heights[neighbor]! ** exponent;
        this.heights[neighbor] = carved > 100 ? 5 : limitHeight(carved);
      }
      range = expansion;
    }
  }

  private modify(range: string, add: number, multiplier: number) {
    const minimum = range === "land" ? 20 : range === "all" ? 0 : +range.split("-")[0]!;
    const maximum = range === "land" || range === "all" ? 100 : +range.split("-")[1]!;
    const land = minimum === 20;
    this.heights = this.heights.map(value => {
      if (value < minimum || value > maximum) return value;
      let height = add ? (land ? Math.max(value + add, 20) : value + add) : value;
      if (multiplier !== 1) height = land ? (height - 20) * multiplier + 20 : height * multiplier;
      return limitHeight(height);
    });
  }

  private smooth(fraction = 2) {
    const source = this.heights;
    this.heights = source.map((height, cell) => {
      const neighbors = this.neighbors(cell);
      const mean = (height + neighbors.reduce((sum, neighbor) => sum + source[neighbor]!, 0)) / (neighbors.length + 1);
      return fraction === 1 ? limitHeight(mean) : limitHeight((height * (fraction - 1) + mean) / fraction);
    });
  }

  private mask(power = 1) {
    const fraction = power ? Math.abs(power) : 1;
    this.heights = this.heights.map((height, cell) => {
      const [x, y] = this.point(cell);
      const nx = 2 * x / (this.columns - 1) - 1;
      const ny = 2 * y / (this.rows - 1) - 1;
      let distance = (1 - nx ** 2) * (1 - ny ** 2);
      if (power < 0) distance = 1 - distance;
      return limitHeight((height * (fraction - 1) + height * distance) / fraction);
    });
  }

  private invert(count: number, axes: string) {
    if (!chance(this.random, count)) return;
    const invertX = axes !== "y";
    const invertY = axes !== "x";
    const source = this.heights;
    this.heights = source.map((_height, cell) => {
      const x = cell % this.columns;
      const y = Math.floor(cell / this.columns);
      const targetX = invertX ? this.columns - x - 1 : x;
      const targetY = invertY ? this.rows - y - 1 : y;
      return source[targetX + targetY * this.columns]!;
    });
  }

  private addStep(tool: HeightmapTool, a2: string, a3: string, a4: string, a5: string) {
    if (tool === "Hill") this.addHill(a2, a3, a4, a5);
    else if (tool === "Pit") this.addPit(a2, a3, a4, a5);
    else if (tool === "Range") this.addRangeLike(a2, a3, a4, a5, 1, .15);
    else if (tool === "Trough") this.addRangeLike(a2, a3, a4, a5, -1, .2);
    else if (tool === "Strait") this.addStrait(a2, a3);
    else if (tool === "Mask") this.mask(+a2);
    else if (tool === "Invert") this.invert(+a2, a3);
    else if (tool === "Add") this.modify(a3, +a2, 1);
    else if (tool === "Multiply") this.modify(a3, 0, +a2);
    else if (tool === "Smooth") this.smooth(+a2);
  }
}

export function generateAzgaarHeightmap(options: AzgaarHeightmapOptions) {
  return new RectangularHeightmapKernel(options).generate(options.template);
}
